import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '../../lib/api';
import { AccountSelect, Money, Empty, fmtDate, downloadCsv, pct, thisYear, inputCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

// Reports for the partners and the CPA: P&L (cash or accrual), balance sheet, general ledger,
// trial balance, member capital. Every report downloads as CSV; the year-end package is a
// row of buttons at the bottom.

const VIEWS = [['pnl', 'Profit and loss'], ['bs', 'Balance sheet'], ['gl', 'General ledger'], ['tb', 'Trial balance'], ['members', 'Member capital']];

export default function ReportsTab({ accounts }) {
  const [view, setView] = useState('pnl');
  const [year, setYear] = useState(thisYear());
  const [from, setFrom] = useState(`${thisYear()}-01-01`);
  const [to, setTo] = useState(`${thisYear()}-12-31`);
  const [basis, setBasis] = useState('accrual');
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const pickYear = (y) => { setYear(y); setFrom(`${y}-01-01`); setTo(`${y}-12-31`); };

  useEffect(() => {
    let alive = true;
    setData(null);
    const path = view === 'pnl' ? `/books/reports/pnl?from=${from}&to=${to}&basis=${basis}`
      : view === 'bs' ? `/books/reports/balance-sheet?as_of=${to}`
        : view === 'gl' ? `/books/reports/gl?from=${from}&to=${to}${accountId ? `&account_id=${accountId}` : ''}`
          : view === 'tb' ? `/books/reports/trial-balance?as_of=${to}`
            : `/books/reports/members?year=${year}`;
    api.get(path).then((d) => { if (alive) { setData(d); setError(''); } }).catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [view, from, to, basis, accountId, year]);

  const exp = async (kind, extra = '') => {
    try { await downloadCsv(`/export/${kind}?year=${year}&from=${from}&to=${to}&as_of=${to}${extra}`); } catch (e) { setError(e.message); }
  };
  const years = [];
  for (let y = thisYear(); y >= 2024; y--) years.push(y);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} className={`rounded-lg px-3 py-1.5 text-sm ${view === k ? 'bg-primary/10 font-medium text-primary' : 'text-[var(--text-secondary)]'}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select className={inputCls} value={year} onChange={(e) => pickYear(Number(e.target.value))} aria-label="Year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {view !== 'members' && view !== 'bs' && view !== 'tb' && <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />}
        {view !== 'members' && <input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} aria-label={view === 'bs' || view === 'tb' ? 'As of' : 'To'} />}
        {view === 'pnl' && (
          <div className="flex rounded-lg border border-[var(--border)] text-sm">
            {['accrual', 'cash'].map((b) => <button key={b} onClick={() => setBasis(b)} className={`px-3 py-1.5 ${basis === b ? 'bg-primary/10 font-medium text-primary' : 'text-[var(--text-secondary)]'}`}>{b === 'cash' ? 'Cash' : 'Accrual'}</button>)}
          </div>
        )}
        {view === 'gl' && <AccountSelect accounts={accounts} value={accountId} onChange={(v) => setAccountId(v || '')} includeInactive placeholder="All accounts" />}
        <button className={`${btn2Cls} ml-auto`} onClick={() => exp({ pnl: 'pnl', bs: 'balance-sheet', gl: 'gl', tb: 'trial-balance', members: 'members' }[view], view === 'pnl' ? `&basis=${basis}` : view === 'gl' && accountId ? `&account_id=${accountId}` : '')}><Download size={14} /> CSV</button>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!data ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p> : (
        <div className={cardCls}>
          {view === 'pnl' && <Pnl p={data} />}
          {view === 'bs' && <BalanceSheet b={data} />}
          {view === 'gl' && <Gl g={data} />}
          {view === 'tb' && <Tb t={data} />}
          {view === 'members' && <Members m={data} />}
        </div>
      )}
      <div className={cardCls}>
        <h3 className="mb-1 font-semibold text-[var(--text-primary)]">Year-end package for the CPA ({year})</h3>
        <p className="mb-2 text-xs text-[var(--text-secondary)]">Download each file for the partnership return and K-1s. Figures use the dates above.</p>
        <div className="flex flex-wrap gap-2">
          {[['pnl', 'P&L (accrual)', '&basis=accrual'], ['pnl', 'P&L (cash)', '&basis=cash'], ['balance-sheet', 'Balance sheet'], ['trial-balance', 'Trial balance'], ['gl', 'General ledger'], ['entries', 'Journal (all lines)'],
            ['members', 'Member capital'], ['member-fees', 'Member fees'], ['payouts', 'Payouts paid'], ['deals', 'Deal payout sheets'], ['accounts', 'Chart of accounts'], ['audit', 'Audit log']].map(([k, l, extra]) => (
            <button key={l} className={btn2Cls} onClick={() => exp(k, extra || '')}><Download size={13} /> {l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Rows({ rows }) {
  return rows.map((r) => (
    <div key={r.account_id || r.name} className="flex justify-between py-0.5 pl-3 text-sm">
      <span className="text-[var(--text-primary)]">{r.code ? `${r.code} ` : ''}{r.name}</span><Money cents={r.amount_cents} />
    </div>
  ));
}
function Total({ label, cents, strong }) {
  return <div className={`flex justify-between border-t border-[var(--border)] py-1 text-sm ${strong ? 'font-semibold' : ''}`}><span>{label}</span><Money cents={cents} /></div>;
}

function Pnl({ p }) {
  return (
    <div>
      <p className="mb-2 text-xs text-[var(--text-secondary)]">{fmtDate(p.from)} to {fmtDate(p.to)}, {p.basis} basis{p.basis === 'cash' ? '. Cash collected on invoices and cash paid on bills are included.' : ''}</p>
      <h4 className="text-xs font-semibold uppercase text-[var(--text-secondary)]">Income</h4><Rows rows={p.income} /><Total label="Total income" cents={p.income_total_cents} />
      <h4 className="mt-2 text-xs font-semibold uppercase text-[var(--text-secondary)]">Cost of goods sold</h4><Rows rows={p.cogs} /><Total label="Total cost of goods sold" cents={p.cogs_total_cents} />
      <Total label="Gross profit" cents={p.gross_profit_cents} strong />
      <h4 className="mt-2 text-xs font-semibold uppercase text-[var(--text-secondary)]">Expenses</h4><Rows rows={p.expense} /><Total label="Total expenses" cents={p.expense_total_cents} />
      <Total label="Net income" cents={p.net_income_cents} strong />
    </div>
  );
}

function BalanceSheet({ b }) {
  return (
    <div>
      <p className="mb-2 text-xs text-[var(--text-secondary)]">As of {fmtDate(b.as_of)}{b.balanced ? '' : '. Does not balance: check the trial balance.'}</p>
      <h4 className="text-xs font-semibold uppercase text-[var(--text-secondary)]">Assets</h4><Rows rows={b.assets} />
      {b.suspense.length > 0 && <div className="pl-3 text-xs text-amber-700 dark:text-amber-400"><Rows rows={b.suspense} /></div>}
      <Total label="Total assets" cents={b.total_assets_cents} strong />
      <h4 className="mt-2 text-xs font-semibold uppercase text-[var(--text-secondary)]">Liabilities</h4><Rows rows={b.liabilities} /><Total label="Total liabilities" cents={b.total_liabilities_cents} />
      <h4 className="mt-2 text-xs font-semibold uppercase text-[var(--text-secondary)]">Equity</h4><Rows rows={b.equity} />
      <Rows rows={[{ name: 'Prior years earnings (undistributed)', amount_cents: b.prior_years_earnings_cents }, { name: 'Net income this year', amount_cents: b.current_year_net_income_cents }]} />
      <Total label="Total equity" cents={b.total_equity_cents} />
      <Total label="Liabilities and equity" cents={b.total_liabilities_cents + b.total_equity_cents} strong />
    </div>
  );
}

function Gl({ g }) {
  if (!g.accounts.length) return <Empty>No activity in this range.</Empty>;
  return g.accounts.map((a) => (
    <div key={a.account_id} className="mb-4 overflow-x-auto">
      <h4 className="font-semibold text-[var(--text-primary)]">{a.code} {a.name}</h4>
      <table className="w-full min-w-[640px] text-sm">
        <thead><tr><th className={thCls}>Date</th><th className={thCls}>Payee / memo</th><th className={thCls}>Split</th><th className={`${thCls} text-right`}>Debit</th><th className={`${thCls} text-right`}>Credit</th><th className={`${thCls} text-right`}>Balance</th></tr></thead>
        <tbody>
          <tr><td className={tdCls} colSpan={5}>Opening balance</td><td className={`${tdCls} text-right`}><Money cents={a.opening_cents} /></td></tr>
          {a.rows.map((r, i) => (
            <tr key={i} className="border-t border-[var(--border)]">
              <td className={`${tdCls} whitespace-nowrap`}>{fmtDate(r.date)}</td><td className={tdCls}>{[r.counterparty, r.memo].filter(Boolean).join(' / ')}</td>
              <td className={`${tdCls} text-xs text-[var(--text-secondary)]`}>{r.split}</td>
              <td className={`${tdCls} text-right`}>{r.debit_cents ? <Money cents={r.debit_cents} /> : ''}</td>
              <td className={`${tdCls} text-right`}>{r.credit_cents ? <Money cents={r.credit_cents} /> : ''}</td>
              <td className={`${tdCls} text-right`}><Money cents={r.balance_cents} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ));
}

function Tb({ t }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-sm">
        <thead><tr><th className={thCls}>Account</th><th className={`${thCls} text-right`}>Debit</th><th className={`${thCls} text-right`}>Credit</th></tr></thead>
        <tbody>
          {t.rows.map((r) => <tr key={r.account_id} className="border-t border-[var(--border)]"><td className={tdCls}>{r.code} {r.name}</td><td className={`${tdCls} text-right`}>{r.debit_cents ? <Money cents={r.debit_cents} /> : ''}</td><td className={`${tdCls} text-right`}>{r.credit_cents ? <Money cents={r.credit_cents} /> : ''}</td></tr>)}
          <tr className="border-t-2 border-[var(--border)] font-semibold"><td className={tdCls}>Total {t.balanced ? '(balanced)' : '(NOT balanced)'}</td><td className={`${tdCls} text-right`}><Money cents={t.total_debit_cents} /></td><td className={`${tdCls} text-right`}><Money cents={t.total_credit_cents} /></td></tr>
        </tbody>
      </table>
    </div>
  );
}

function Members({ m }) {
  return (
    <div className="overflow-x-auto">
      <p className="mb-2 text-xs text-[var(--text-secondary)]">{m.year} net income <Money cents={m.net_income_cents} />, shown split by ownership for reference. The CPA makes the final allocation on the return. {m.note}</p>
      <table className="w-full min-w-[720px] text-sm">
        <thead><tr>{['Member', 'Ownership', 'Opening capital', 'Contributions', 'Share of net income', 'Distributions', 'Ending capital', 'Reimbursements owed'].map((h) => <th key={h} className={`${thCls} ${h === 'Member' ? '' : 'text-right'}`}>{h}</th>)}</tr></thead>
        <tbody>
          {m.members.map((r) => (
            <tr key={r.member} className="border-t border-[var(--border)]">
              <td className={tdCls}>{r.member}</td><td className={`${tdCls} text-right`}>{pct(r.ownership_bps)}</td>
              {['opening_capital_cents', 'contributions_cents', 'allocated_net_income_cents', 'distributions_cents', 'ending_capital_cents', 'reimbursements_payable_cents'].map((k) => <td key={k} className={`${tdCls} text-right`}><Money cents={r[k]} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {m.partner_investments_owed && (
        <p className="mt-2 text-xs text-[var(--text-secondary)]">Partner Investments tab, still owed: {m.partner_investments_owed.map((x) => `${x.member} $${(x.owed_cents / 100).toFixed(2)}`).join(', ')}. Record a reimbursement in the ledger (Reimbursements Payable) when it is paid.</p>
      )}
    </div>
  );
}
